import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapPin } from 'lucide-react';
import SiteMobileHeader from '../components/SiteMobileHeader';
import StickyNavigation from '../components/StickyNavigation';
import MailingListModal from '../components/MailingListModal';
import SectionContainer from '../components/SectionContainer';
import HomeContactFooter from '../components/HomeContactFooter';
import homeHeroLogo from '../assets/Group12.svg';
import frame205Logo from '../assets/Frame 205.svg';
import vectorGraphic from '../assets/Vector.svg';
import iconsaxAiHomepage from '../assets/shared/icons/home-amenity.svg';
import { clearContactState, submitMailingListForm } from '../store/slices/contactSlice';
import { validateMailingListForm } from '../utils/mailingListValidation';
import { showToast } from '../utils/toast';
import useInnovationCardScrollReveal from '../hooks/useInnovationCardScrollReveal';
import useReplayOnScrollReveal from '../hooks/useReplayOnScrollReveal';
import {
  asset01,
  asset18Image,
  asset19Image,
  asset20,
  eiraMobileShowcaseImage,
  elmoraMobileShowcaseImage,
  homeHeroPromoVideoDesktop,
  homeHeroPromoVideoMobile,
  humanCentredDesignDesktop,
  shot1Image,
  shot2Image,
  shot3Image,
  shot4Image,
  shot5Image,
  shot6Image,
} from '../cdn/landingMedia';
import './LandingPage.css';
import '../styles/SitePageShell.css';

const FEATURED_INTRO_COPY = {
  titleLines: ['The New era of', 'luxury'],
  subtitleLines: ['To create worlds where luxury holds the', 'final word'],
};

const FEATURED_CAROUSEL_SLIDES = [
  {
    id: 'intro-image',
    image: humanCentredDesignDesktop,
    alt: 'The new era of luxury',
  },
  {
    id: 'sustainability',
    image: shot4Image,
    alt: 'The new standard of sustainability',
    titleLines: ['The New Standard of', 'Sustainability'],
    subtitleLines: ['To build responsibly, ensuring what stands', 'today will be cherished tomorrow.'],
  },
  {
    id: 'quality',
    image: shot3Image,
    alt: 'The new benchmark of quality',
    titleLines: ['The New Benchmark of', 'Quality'],
    subtitleLines: ['To uphold an uncompromising standard in', 'everything that carries our name.'],
    mobileSubtitleLines: ['To uphold an uncompromising standard', 'in everything that carries our name.'],
  },
  {
    id: 'innovation',
    image: shot6Image,
    alt: 'The new language of innovation',
    titleLines: ['The New Language of', 'Innovation'],
    subtitleLines: ['To shape what has never been imagined,', 'and make it enduring.'],
  },
  {
    id: 'closing',
    image: shot5Image,
    alt: 'The new vision of human centred design',
    titleLines: ['The New Vision of Human', 'Centred Design'],
    subtitleLines: ['To place those we build for at the centre', 'of every decision we make.'],
  },
];
const EIRA_SHOWCASE_SLIDES = [
  { id: 'eira', image: shot1Image, alt: 'Eira showcase' },
  { id: 'elmora', image: shot2Image, alt: 'Elmora showcase' },
];
const EIRA_SHOWCASE_MOBILE_IMAGES = {
  eira: eiraMobileShowcaseImage,
  elmora: elmoraMobileShowcaseImage,
};
const FEATURED_CAROUSEL_HIDDEN_CLIP = 'inset(100% 0 0 0)';
const CAROUSEL_REVEALED_CLIP = 'inset(0 0 0 0)';
const MAIL_MODAL_CLOSE_MS = 620;

const getEiraSlideWipe = (idx, { baseIndex, wipe }) => {
  if (idx <= baseIndex) {
    return 1;
  }
  if (idx === baseIndex + 1 && wipe > 0) {
    return wipe;
  }
  return 0;
};

const EiraShowcaseSlideOverlay = ({ slideId, interactive = false }) => (
  <>
    {slideId === 'elmora' ? (
      <div
        className="pointer-events-none absolute inset-0 z-[2]"
        style={{
          background:
            'linear-gradient(180deg, #000000 -27.03%, rgba(0, 0, 0, 0.47008) 9.36%, rgba(0, 0, 0, 0) 66.33%)',
        }}
        aria-hidden="true"
      />
    ) : null}

    {slideId === 'eira' ? (
      <div className="pointer-events-none absolute right-[clamp(16px,3vw,40px)] top-[clamp(16px,3vw,30px)] z-[3]">
        <span className="eira-showcase-badge inline-flex h-[40.6px] w-[156.6px] items-center justify-center bg-[#FFF7EA] font-['Arial',sans-serif] text-[11.9px] font-bold uppercase leading-[19.35px] tracking-[2.58px] text-[#BC8639] lg:h-[54px] lg:w-[209px] lg:text-[15.87px] lg:leading-[25.81px] lg:tracking-[3.44px]">
          New Launch
        </span>
      </div>
    ) : null}

    <div className="eira-showcase-copy pointer-events-none absolute bottom-[clamp(18px,4vw,42px)] left-[clamp(18px,4vw,44px)] z-[3] flex max-w-[min(88vw,420px)] flex-col items-start text-left text-white">
      {slideId === 'eira' ? (
        <>
          <h3 className="eira-showcase-title m-0 font-['Poppins',sans-serif] text-[28.89px] font-normal uppercase leading-[100%] tracking-[0] text-white align-middle lg:text-[38.52px]">
            Eira
          </h3>
          <p className="eira-showcase-meta m-0 mt-[8px] inline-flex items-center gap-[8px] font-['Poppins',sans-serif] text-[16px] font-normal leading-[100%] tracking-[0]">
            <MapPin className="h-[18px] w-[18px] shrink-0" strokeWidth={1.5} aria-hidden />
            <span>Production city, dubai</span>
          </p>
          <p className="eira-showcase-meta m-0 mt-[8px] inline-flex items-center gap-[8px] font-['Poppins',sans-serif] text-[16px] font-normal leading-[100%] tracking-[0]">
            <img
              src={iconsaxAiHomepage}
              alt=""
              className="h-[18px] w-[18px] shrink-0 object-contain brightness-0 invert"
              loading="lazy"
              aria-hidden="true"
            />
            <span>Studio, 1-bed residences</span>
          </p>
          <a
            href="https://eiradubai.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={`eira-showcase-cta mt-[22px] inline-flex h-[34px] w-[118px] items-center justify-center rounded-[30631902px] bg-[#BC8639] font-['Poppins',sans-serif] text-[9px] font-medium leading-[18px] tracking-[0.3px] text-center text-white no-underline lg:h-[45px] lg:w-[157px] lg:text-[12.03px] lg:leading-[24.07px] lg:tracking-[0.4px] lg:transition-transform lg:duration-200 lg:hover:-translate-y-[1px] ${
              interactive ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
            data-eira-cta
            tabIndex={interactive ? 0 : -1}
            aria-hidden={interactive ? undefined : true}
          >
            Explore Eira
          </a>
        </>
      ) : (
        <>
          <h3 className="eira-showcase-title m-0 font-['Poppins',sans-serif] text-[28.89px] font-normal uppercase leading-[100%] tracking-[0] text-white align-middle lg:text-[38.52px]">
            Elmora
          </h3>
          <p className="eira-showcase-meta m-0 mt-[8px] inline-flex items-center gap-[8px] font-['Poppins',sans-serif] text-[16px] font-normal leading-[100%] tracking-[0]">
            <MapPin className="h-[18px] w-[18px] shrink-0" strokeWidth={1.5} aria-hidden />
            <span>Off-Sheikh Zayed Road</span>
          </p>
          <p className="eira-showcase-meta m-0 mt-[8px] inline-flex items-center gap-[8px] font-['Poppins',sans-serif] text-[16px] font-normal leading-[100%] tracking-[0]">
            <img
              src={iconsaxAiHomepage}
              alt=""
              className="h-[18px] w-[18px] shrink-0 object-contain brightness-0 invert"
              loading="lazy"
              aria-hidden="true"
            />
            <span>Studio, 1-bed and 2-bed residences</span>
          </p>
          <a
            href="https://elmoradubai.ae/"
            target="_blank"
            rel="noopener noreferrer"
            className={`eira-showcase-cta mt-[22px] inline-flex h-[34px] w-[118px] items-center justify-center rounded-[30631902px] bg-[#BC8639] px-4 font-['Poppins',sans-serif] text-[9px] font-medium leading-[18px] tracking-[0.3px] text-center text-white no-underline lg:h-[45px] lg:w-[157px] lg:text-[12.03px] lg:leading-[24.07px] lg:tracking-[0.4px] lg:transition-transform lg:duration-200 lg:hover:-translate-y-[1px] ${
              interactive ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
            data-eira-cta
            tabIndex={interactive ? 0 : -1}
            aria-hidden={interactive ? undefined : true}
          >
            Explore Elmora
          </a>
        </>
      )}
    </div>
  </>
);

const FEATURED_COPY_GAP_MULTIPLIER = 3;

const getFeaturedCopyGapPx = (viewportWidth, vh) => {
  if (viewportWidth >= 1024) {
    const lpScale = viewportWidth >= 1440
      ? Math.min(viewportWidth / 1440, 1920 / 1440)
      : 1;
    return 36 * lpScale * FEATURED_COPY_GAP_MULTIPLIER;
  }
  return Math.min(36 * FEATURED_COPY_GAP_MULTIPLIER, Math.max(14 * FEATURED_COPY_GAP_MULTIPLIER, vh * 0.032 * FEATURED_COPY_GAP_MULTIPLIER));
};

const getFeaturedSlideWipe = (idx, { baseIndex, wipe, introReveal }) => {
  if (idx === 0 && introReveal < 1) {
    return introReveal;
  }
  if (idx <= baseIndex) {
    return 1;
  }
  if (idx === baseIndex + 1 && wipe > 0) {
    return wipe;
  }
  return 0;
};

const getFeaturedSlideClip = (wipeProgress) => {
  if (wipeProgress >= 1) return CAROUSEL_REVEALED_CLIP;
  if (wipeProgress <= 0) return FEATURED_CAROUSEL_HIDDEN_CLIP;
  return `inset(${(1 - wipeProgress) * 100}% 0 0 0)`;
};

const getFeaturedCopyTopPx = ({
  idx,
  slideWipe,
  isIncoming,
  isIntroPhase,
  promiseCopyTop,
  copyGap,
  vh,
}) => {
  if (idx === 0 && isIntroPhase && promiseCopyTop != null) {
    return promiseCopyTop;
  }
  if (isIncoming && slideWipe > 0 && slideWipe < 1) {
    return ((1 - slideWipe) * vh) + copyGap;
  }
  return copyGap;
};

const applyFeaturedCarouselFrame = ({
  slideEls,
  copyEls,
  baseIndex,
  wipe,
  introPhaseActive,
  introReveal,
  vh,
  copyGap,
}) => {
  const slideCount = FEATURED_CAROUSEL_SLIDES.length;
  const resolvedIntroReveal = introPhaseActive ? introReveal : 1;

  for (let idx = 0; idx < slideCount; idx += 1) {
    const slideEl = slideEls[idx];
    if (!slideEl) continue;

    const slideWipe = getFeaturedSlideWipe(idx, {
      baseIndex,
      wipe,
      introReveal: resolvedIntroReveal,
    });
    const isIncoming = idx === baseIndex + 1 && wipe > 0;
    const useIntroCopy = idx === 0 && baseIndex === 0;
    const isIntroPhase = idx === 0 && introPhaseActive;

    if (isIntroPhase) {
      slideEl.style.removeProperty('clip-path');
    } else {
      slideEl.style.clipPath = getFeaturedSlideClip(slideWipe);
    }

    const slide = FEATURED_CAROUSEL_SLIDES[idx];
    const showCopy = idx === 0
      ? Boolean(FEATURED_INTRO_COPY.titleLines?.length && FEATURED_INTRO_COPY.subtitleLines?.length)
      : Boolean(!slide.hideCopy && slide.titleLines?.length && slide.subtitleLines?.length);
    const copyVisible = showCopy && (
      (idx === baseIndex && slideWipe > 0.05)
      || (isIncoming && wipe > 0 && wipe < 1)
      || useIntroCopy
    );

    const copyEl = copyEls[idx];
    if (copyEl) {
      copyEl.style.visibility = copyVisible ? 'visible' : 'hidden';
      copyEl.style.opacity = copyVisible ? '1' : '0';
      copyEl.classList.toggle('featured-hero-copy--intro-sync', isIntroPhase);
      if (isIntroPhase) {
        copyEl.style.removeProperty('top');
      } else {
        copyEl.style.top = `${getFeaturedCopyTopPx({
          idx,
          slideWipe,
          isIncoming,
          isIntroPhase: false,
          promiseCopyTop: null,
          vh,
          copyGap,
        })}px`;
      }
    }

    const img = slideEl.querySelector('img');
    if (img) {
      img.toggleAttribute('aria-hidden', !(slideWipe > 0.05 || useIntroCopy));
    }
  }
};

const applyEiraCarouselFrame = ({ slideEls, baseIndex, wipe }) => {
  EIRA_SHOWCASE_SLIDES.forEach((_, idx) => {
    const slideEl = slideEls[idx];
    if (!slideEl) return;

    const slideWipe = getEiraSlideWipe(idx, { baseIndex, wipe });
    slideEl.style.clipPath = getFeaturedSlideClip(slideWipe);
    const isInteractive = slideWipe > 0.05;

    const img = slideEl.querySelector('img');
    if (img) {
      img.toggleAttribute('aria-hidden', !isInteractive);
    }

    slideEl.querySelectorAll('[data-eira-cta]').forEach((cta) => {
      cta.classList.toggle('pointer-events-auto', isInteractive);
      cta.classList.toggle('pointer-events-none', !isInteractive);
      cta.tabIndex = isInteractive ? 0 : -1;
      cta.toggleAttribute('aria-hidden', !isInteractive);
    });
  });
};

/** Home innovation cards — Figma 1920 (images 584×629); scales from 1440 via --lp-scale */
const INNOVATION_CARDS = [
  {
    id: 'privilege',
    image: asset20,
    imageAlt: 'Extraordinary living experience',
    imageLeft: true,
    titleLines: ['The privilege of', 'Extraordinary', 'Living'],
    mobileTitleLines: ['The privilege of', 'Extraordinary Living'],
    body: 'Every experience we create is orchestrated to offer a lifestyle defined by stature and distinction.',
    bodyLines: [
      'Every experience we create is orchestrated to offer',
      'a lifestyle defined by stature and distinction.',
    ],
  },
  {
    id: 'masterclass',
    image: asset19Image,
    imageAlt: 'Master class in design',
    imageLeft: false,
    titleLines: ['Every creation, A', 'Master class in', 'Design'],
    mobileTitleLines: ['Every creation, A', 'Master class in Design'],
    body: 'We build with the gravity of legacy. Every element is considered, every material chosen with purpose, each creation standing as an emblem of absolute mastery.',
    bodyLines: [
      'We build with the gravity of legacy. Every element is',
      'considered, every material chosen with purpose, each',
      'creation standing as an emblem of absolute mastery.',
    ],
  },
  {
    id: 'detail',
    image: asset01,
    imageAlt: 'Obsession for detail',
    imageLeft: true,
    titleLines: ['Crafted with an', 'Obsession for', 'Detail'],
    mobileTitleLines: ['Crafted with an', 'Obsession for Detail'],
    body: 'Greatness lives in what most overlook. We made sure every detail becomes a part of the grand experience.',
    bodyLines: [
      'Greatness lives in what most overlook. We made sure',
      'every detail becomes a part of the grand experience.',
    ],
  },
  {
    id: 'new-earth',
    image: asset18Image,
    imageAlt: 'Built with care',
    imageLeft: false,
    titleLines: ['A new earth', 'Build with', 'Care'],
    mobileTitleLines: ['A new earth', 'Build with Care'],
    body: 'Each creation is made to endure, to inspire, and to leave a legacy of extraordinary magnificence, creating a better world for tomorrow.',
    bodyLines: [
      'Each creation is made to endure, to inspire, and to',
      'leave a legacy of extraordinary magnificence,',
      'creating a better world for tomorrow',
    ],
  },
];

const innovationCardBodyClass =
  "innovation-desktop-body m-0 flex flex-col gap-y-[2px] pt-[8px] align-middle text-left font-['Poppins',sans-serif] text-[15px] font-light leading-[23px] tracking-[0px] text-[#0A0A0AC7]";

const InnovationAnimatedLine = ({ text, className = '' }) => (
  <span className={className}>
    {[...text].map((char, charIdx) => (
      <span
        key={`${text}-${charIdx}`}
        className="innovation-card-char"
        aria-hidden={char === ' ' ? true : undefined}
      >
        {char === ' ' ? '\u00A0' : char}
      </span>
    ))}
  </span>
);

const FeaturedHeroCopy = forwardRef(({
  titleLines,
  subtitleLines,
  visible = true,
  copyTop = 0,
  introSync = false,
}, ref) => {
  if (!visible) return null;

  return (
  <div
    ref={ref}
    className={`featured-hero-copy pointer-events-none absolute left-[clamp(20px,5vw,72px)] text-left${introSync ? ' featured-hero-copy--intro-sync' : ''}`}
    style={{ visibility: 'hidden', opacity: 0, ...(introSync ? {} : { top: `${copyTop}px` }) }}
  >
    <h2 className="featured-hero-copy__title m-0 flex flex-col gap-[28px] font-['Poppins',sans-serif] text-[40px] font-medium uppercase leading-[100%] tracking-[0px] text-white">
      {titleLines.map((line) => (
        <span key={line} className="featured-hero-copy__title-line block">
          {line}
        </span>
      ))}
    </h2>
    <span
      className="featured-hero-copy__rule mt-[32px] block w-[65.2568px] border-t-[2px] border-solid border-white"
      aria-hidden="true"
    />
    <p className="featured-hero-copy__subtitle m-0 mt-[32px] flex max-w-[min(92vw,520px)] flex-col gap-[14px] font-['Poppins',sans-serif] text-[20px] font-medium leading-[100%] tracking-[0px] text-white">
      {subtitleLines.map((line) => (
        <span key={line} className="featured-hero-copy__subtitle-line block">
          {line}
        </span>
      ))}
    </p>
  </div>
  );
});
FeaturedHeroCopy.displayName = 'FeaturedHeroCopy';

const InnovationMobileCopy = ({ card }) => {
  const headingLines = card.mobileTitleLines || card.titleLines;
  return (
  <div className="innovation-card-copy-mobile w-full min-w-0 lg:hidden">
    <h3 className="innovation-card-title-stack m-0 items-start">
      {headingLines.map((line) => (
        <span key={line} className="innovation-card-title-line innovation-card-title-line--mobile">
          {line}
        </span>
      ))}
    </h3>
    <p className="innovation-mobile-body m-0 mt-[14px] font-['Poppins',sans-serif] text-[14px] font-light leading-[1.5] tracking-[0] text-[rgba(10,10,10,0.78)]">
      {card.body}
    </p>
  </div>
  );
};

const LandingPage = () => {
  const dispatch = useDispatch();
  const contactSubmitting = useSelector((state) => state.contact.loading);
  const featuredRevealRef = useRef(null);
  const featuredRevealPinHostRef = useRef(null);
  const featuredRevealPinRef = useRef(null);
  const featuredCurtainRef = useRef(null);
  const promiseStripRef = useRef(null);
  const featuredSlideElsRef = useRef([]);
  const featuredCopyElsRef = useRef([]);
  const eiraWipeRunwayRef = useRef(null);
  const eiraRevealPinHostRef = useRef(null);
  const eiraRevealPinRef = useRef(null);
  const eiraSlideElsRef = useRef([]);
  const luxuryThoughtfullyRef = useRef(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailClosing, setMailClosing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [promotionalConsent, setPromotionalConsent] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 402));
  const innovationSectionRef = useRef(null);
  const heroSectionRef = useRef(null);
  const heroPromoVideoRef = useRef(null);

  useInnovationCardScrollReveal(innovationSectionRef);
  useReplayOnScrollReveal('.landing-page .home-reveal-on-scroll', 'is-visible', 0.12, '80px 0px -10% 0px', false);

  const scrollToMainContent = () => {
    const target = document.getElementById('innovation-start');
    if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const curtain = featuredCurtainRef.current;
    const runway = featuredRevealRef.current;
    const host = featuredRevealPinHostRef.current;
    const pin = featuredRevealPinRef.current;
    const promise = promiseStripRef.current;
    const luxury = luxuryThoughtfullyRef.current;
    if (!curtain || !runway || !host || !pin) return undefined;

    let rafId = null;
    const carouselState = {
      baseIndex: 0,
      wipe: 0,
      introPhaseActive: false,
      introReveal: 0,
    };

    const update = () => {
      rafId = null;

      const curtainRect = curtain.getBoundingClientRect();
      const runwayRect = runway.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      const luxuryRect = luxury?.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const copyGap = getFeaturedCopyGapPx(vw, vh);

      const revealStarted = curtainRect.top <= 0;
      const luxuryOnScreen = Boolean(luxuryRect && luxuryRect.bottom > 0);
      const luxuryPeeling = Boolean(luxuryRect && luxuryRect.top <= 0 && luxuryRect.bottom > 0);
      const postRunwayBridge = revealStarted
        && runwayRect.bottom <= 0
        && luxuryOnScreen;
      const shouldLock = revealStarted && (
        curtainRect.bottom > 0 || runwayRect.bottom > 0 || hostRect.bottom > 0 || postRunwayBridge
      );

      host.classList.toggle('featured-reveal-pin-host--reserve', revealStarted);
      pin.classList.toggle('featured-reveal-pin--locked', shouldLock);
      pin.classList.remove('featured-reveal-pin--settled');
      pin.classList.toggle('featured-reveal-pin--bridge-hidden', luxuryPeeling);

      if (curtainRect.bottom > 0) {
        const introReveal = Math.max(0, Math.min(1, Math.max(0, -curtainRect.top) / vh));
        pin.style.setProperty('--featured-intro-reveal', String(introReveal));
        if (promise) {
          const promiseRect = promise.getBoundingClientRect();
          const nextTop = promiseRect.bottom > 0 ? promiseRect.bottom + copyGap : copyGap;
          pin.style.setProperty('--featured-copy-top', `${nextTop}px`);
        }
        carouselState.baseIndex = 0;
        carouselState.wipe = 0;
        carouselState.introPhaseActive = true;
        carouselState.introReveal = introReveal;
      } else {
        pin.style.removeProperty('--featured-intro-reveal');
        pin.style.removeProperty('--featured-copy-top');
        carouselState.introPhaseActive = false;
        carouselState.introReveal = 1;

        const runwayScroll = runway.offsetHeight - vh;
        if (runwayScroll > 0) {
          const scrolled = Math.max(0, Math.min(runwayScroll, -runwayRect.top));
          const slideCount = FEATURED_CAROUSEL_SLIDES.length;
          const maxStep = Math.max(1, slideCount - 1);
          const scrollProgress = scrolled / runwayScroll;
          const raw = scrollProgress * maxStep;
          const atLastSlide = scrollProgress >= 0.998 || scrolled >= runwayScroll - 1;
          carouselState.baseIndex = atLastSlide
            ? slideCount - 1
            : Math.min(slideCount - 1, Math.floor(raw + 1e-6));
          carouselState.wipe = carouselState.baseIndex >= slideCount - 1
            ? 1
            : raw - Math.floor(carouselState.baseIndex);
        }
      }

      applyFeaturedCarouselFrame({
        slideEls: featuredSlideElsRef.current,
        copyEls: featuredCopyElsRef.current,
        baseIndex: carouselState.baseIndex,
        wipe: carouselState.wipe,
        introPhaseActive: carouselState.introPhaseActive,
        introReveal: carouselState.introReveal,
        vh,
        copyGap,
      });
    };

    const schedule = () => {
      if (rafId == null) {
        rafId = window.requestAnimationFrame(update);
      }
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      pin.classList.remove('featured-reveal-pin--locked');
      pin.classList.remove('featured-reveal-pin--settled');
      pin.classList.remove('featured-reveal-pin--bridge-hidden');
      host.classList.remove('featured-reveal-pin-host--reserve');
    };
  }, []);

  useEffect(() => {
    const luxury = luxuryThoughtfullyRef.current;
    const runway = eiraWipeRunwayRef.current;
    const host = eiraRevealPinHostRef.current;
    const pin = eiraRevealPinRef.current;
    if (!luxury || !runway || !host || !pin) return undefined;

    let rafId = null;
    const carouselState = { baseIndex: 0, wipe: 0 };

    const update = () => {
      rafId = null;

      const luxuryRect = luxury.getBoundingClientRect();
      const runwayRect = runway.getBoundingClientRect();
      const vh = window.innerHeight;

      const luxuryCovering = luxuryRect.bottom > 0;
      const shouldLock = luxuryRect.bottom < vh && runwayRect.bottom > 0;
      const shouldSettle = luxuryRect.top <= 0 && !shouldLock;

      host.classList.toggle('eira-reveal-pin-host--reserve', shouldLock || shouldSettle);
      pin.classList.toggle('eira-reveal-pin--locked', shouldLock);
      pin.classList.toggle('eira-reveal-pin--settled', shouldSettle);

      if (shouldLock && luxuryCovering) {
        const clipTop = Math.max(0, Math.min(vh, luxuryRect.bottom));
        pin.style.clipPath = clipTop > 0.5 ? `inset(${clipTop}px 0 0 0)` : 'inset(0 0 0 0)';
      } else {
        pin.style.clipPath = '';
      }

      const runwayScroll = runway.offsetHeight - vh;
      if (luxuryCovering || runwayScroll <= 0) {
        carouselState.baseIndex = 0;
        carouselState.wipe = 0;
      } else {
        const scrolled = Math.max(0, Math.min(runwayScroll, -runwayRect.top));
        const slideCount = EIRA_SHOWCASE_SLIDES.length;
        const maxStep = Math.max(1, slideCount - 1);
        const scrollProgress = runwayScroll > 0 ? scrolled / runwayScroll : 0;
        const raw = scrollProgress * maxStep;
        const atLastSlide = scrollProgress >= 0.998 || scrolled >= runwayScroll - 1;
        carouselState.baseIndex = atLastSlide
          ? slideCount - 1
          : Math.min(slideCount - 1, Math.floor(raw + 1e-6));
        carouselState.wipe = carouselState.baseIndex >= slideCount - 1
          ? 1
          : raw - Math.floor(carouselState.baseIndex);
      }

      applyEiraCarouselFrame({
        slideEls: eiraSlideElsRef.current,
        baseIndex: carouselState.baseIndex,
        wipe: carouselState.wipe,
    });
  };

    const schedule = () => {
      if (rafId == null) {
        rafId = window.requestAnimationFrame(update);
      }
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      host.classList.remove('eira-reveal-pin-host--reserve');
      pin.classList.remove('eira-reveal-pin--locked');
      pin.classList.remove('eira-reveal-pin--settled');
      pin.style.clipPath = '';
    };
  }, []);

  useEffect(() => {
    const hero = heroSectionRef.current;
    if (!hero) return undefined;

    let rafId = null;

    const update = () => {
      rafId = null;
      const pastHero = window.scrollY > window.innerHeight * 0.98;
      hero.classList.toggle('landing-hero--past', pastHero);
    };

    const schedule = () => {
      if (rafId == null) {
        rafId = window.requestAnimationFrame(update);
      }
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      hero.classList.remove('landing-hero--past');
    };
  }, []);

  const openMailModal = () => {
    setMailClosing(false);
    setMailOpen(true);
  };

  const closeMailModal = () => {
    if (mailClosing || !mailOpen) return;
    setMailClosing(true);
    window.setTimeout(() => {
      setMailOpen(false);
      setMailClosing(false);
    }, MAIL_MODAL_CLOSE_MS);
  };

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    const validation = validateMailingListForm({ fullName, email, phone });

    if (!validation.isValid) {
      showToast(validation.error, 'error');
      return;
    }

    const { name, email: emailValue, contactNumber } = validation.values;

    try {
      await dispatch(
        submitMailingListForm({
          name,
          email: emailValue,
          contactNumber,
          promotionalConsent,
          sourcePage: 'home',
        })
      ).unwrap();
      showToast('Thanks! We will keep you informed.', 'success');
      setFullName('');
      setEmail('');
      setPhone('');
      setPromotionalConsent(false);
      closeMailModal();
    } catch (err) {
      showToast(typeof err === 'string' ? err : 'Something went wrong. Please try again.', 'error');
    } finally {
      dispatch(clearContactState());
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const snapClassName = 'landing-page-snap-root';
    // Desktop scroll is driven by long pinned runways; mandatory snap fights wheel scrolling.
    const shouldSnap = false;

    root.classList.toggle(snapClassName, shouldSnap);
    body.classList.toggle(snapClassName, shouldSnap);

    return () => {
      root.classList.remove(snapClassName);
      body.classList.remove(snapClassName);
    };
  }, [viewportWidth]);

  useEffect(() => {
    const handleViewportResize = () => setViewportWidth(window.innerWidth || 402);
    handleViewportResize();
    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  const heroPromoVideoSrc =
    viewportWidth >= 1024 ? homeHeroPromoVideoDesktop : homeHeroPromoVideoMobile;

  /** Remount on breakpoint swap + some browsers need explicit play() after src change. */
  useEffect(() => {
    const el = heroPromoVideoRef.current;
    if (!el) return undefined;
    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    el.addEventListener('canplay', tryPlay);
    return () => el.removeEventListener('canplay', tryPlay);
  }, [heroPromoVideoSrc]);

  return (
    <div className={`landing-page site-page-shell${viewportWidth >= 1024 ? ' landing-page--desktop-snap' : ''}`}>
      <SiteMobileHeader
        variant="homeHero"
        logoSrc={homeHeroLogo}
        barClassName="home-top-bar"
        onEnquireClick={openMailModal}
      />
      {/* Mobile-first hero intro */}
      <div className="landing-snap-section landing-snap-section--hero relative mx-auto h-screen min-h-screen w-full" style={{ height: '100dvh', minHeight: '100dvh' }}>
        <section
          ref={heroSectionRef}
          className="fixed left-0 top-0 z-[1] m-0 flex h-screen min-h-screen w-screen max-w-none flex-col items-center justify-between overflow-hidden bg-[#020202] px-0 pb-[46px] pt-[22px] lg:pt-0"
          style={{ height: '100dvh', minHeight: '100dvh' }}
          aria-label="NeoTerra hero"
        >
        <video
          ref={heroPromoVideoRef}
          key={heroPromoVideoSrc}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center lg:[animation:homeHeroVideoDrift_20s_ease-in-out_infinite_alternate]"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src={heroPromoVideoSrc} type="video/mp4" />
        </video>
        <div className="absolute inset-0 z-[1] bg-[radial-gradient(circle_at_20%_10%,rgba(0,0,0,0.28),transparent_35%),radial-gradient(circle_at_85%_90%,rgba(188,142,92,0.08),transparent_40%),rgba(0,0,0,0.45)] lg:bg-[radial-gradient(circle_at_50%_0%,rgba(0,0,0,0.18),transparent_42%),rgba(0,0,0,0.18)]" />
        <div className="absolute left-0 top-0 z-[2] h-px w-full bg-white/70 lg:hidden" />
        <div className="hero-bottom-actions pointer-events-none absolute inset-x-0 bottom-0 z-[2] hidden w-full max-w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end justify-items-center pb-[clamp(28px,4.5vh,48px)] lg:grid">
          <button
            type="button"
            className="relative z-[2] col-[2] hidden h-[42.2039909362793px] w-[25.314611434936523px] translate-y-2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 opacity-100 [pointer-events:auto] [-webkit-tap-highlight-color:transparent] [touch-action:manipulation] lg:flex"
            onClick={scrollToMainContent}
            aria-label="Scroll to main content"
          >
            <span
              className="box-border flex h-[42.2039909362793px] w-[25.314611434936523px] shrink-0 items-start justify-center rounded-[41785312px] border-[1.25px] border-solid border-[#c9a961] px-[8.25px] pt-[20.94px] lg:[animation:homeMouseBounce_1500ms_ease-in-out_infinite] [transform:translate3d(0,0,0)] motion-reduce:animate-none"
              aria-hidden="true"
            >
              <span className="h-[6.3307085037231445px] w-[6.3307085037231445px] shrink-0 rounded-[41785312px] bg-[#bc8639] lg:[animation:homeMouseBounce_1500ms_ease-in-out_infinite] [transform:translate3d(0,0,0)] motion-reduce:animate-none" />
            </span>
          </button>
        </div>
        </section>
      </div>

      <StickyNavigation
        wrapperClassName="home-navigation-sticky lg:hidden"
        onInquireClick={openMailModal}
      />

      <div className="featured-experience relative w-full">
      <div ref={featuredCurtainRef} className="featured-scroll-curtain relative z-[20] w-full">
      <div className="relative z-[3] w-full bg-[#FFFFFF]">
      <section
        ref={innovationSectionRef}
        className="landing-snap-section landing-snap-section--innovation innovation-feature-section relative z-[3] flex flex-col items-center overflow-hidden bg-[#FFFFFF] pb-[calc(env(safe-area-inset-bottom,0px)+clamp(16px,4.2vh,30px))] pt-[clamp(36px,5.2vh,56px)] lg:justify-center lg:gap-y-[clamp(40px,5vw,72px)] lg:overflow-x-visible lg:overflow-y-clip lg:px-[8px] lg:pb-[calc(env(safe-area-inset-bottom,0px)+clamp(24px,6.3vh,45px))] lg:pt-0"
        aria-label="Innovation"
        id="innovation-start"
      >
        <div className="home-reveal-on-scroll home-luxury-mobile relative z-[6] mb-[56px] w-full bg-transparent text-center lg:hidden">
          <p className="home-luxury-mobile__title">
            <span>LUXURY</span>
            <span>DONE THOUGHTFULLY</span>
          </p>
          <p className="home-luxury-mobile__lead">
              At Neoterra, we believe true luxury is shaped through intent and precision. Guided by a
              commitment to refined living, we craft not just homes but immersive experiences where
              every detail is purposefully considered and seamlessly brought together.
            </p>
          <p className="home-luxury-mobile__lead home-luxury-mobile__lead--follow">
            <span>Because when luxury is done</span>
            <span>thoughtfully,</span>
          </p>
          <p className="home-luxury-mobile__accent">
            <span>Every space feels elevated,</span>
            <span>Every moment feels effortless, and</span>
            <span>Every experience feels distinctly yours!</span>
          </p>
          </div>

        <div className="innovation-cards-rail innovation-cards-rail--desktop relative z-[1] mx-auto flex w-full flex-col gap-y-10 lg:gap-y-[18vh] lg:px-0">
          {INNOVATION_CARDS.map((card, index) => {
            const imageLeft = card.imageLeft;
            return (
              <article
                key={card.id}
                className={`home-reveal-on-scroll innovation-feature-card relative flex w-full flex-col gap-[22px] lg:grid lg:grid-cols-2 lg:items-center lg:gap-x-[4vw] lg:gap-y-3 ${
                  imageLeft ? 'innovation-feature-card--fill-ltr' : 'innovation-feature-card--fill-rtl'
                }`}
              >
                {/* Vertical lines — centre of page (not text column) */}
                <div
                  className="innovation-card-vlines pointer-events-none absolute inset-y-0 left-1/2 z-[2] hidden -translate-x-1/2 lg:flex lg:flex-col lg:justify-between"
                  aria-hidden="true"
                >
                  <span className="innovation-card-vline innovation-card-vline--top h-[123px] w-0 border-l-[1px] border-solid border-[#BC8639]" />
                  <span className="innovation-card-vline innovation-card-vline--bottom h-[123px] w-0 border-l-[1px] border-solid border-[#BC8639]" />
        </div>

                <div
                  className={`w-full ${
                    imageLeft ? 'lg:justify-self-start' : 'lg:order-2 lg:justify-self-end'
                  }`}
                >
                  <div
                    className={`innovation-card-image-wrap overflow-hidden ${
                      imageLeft ? 'lg:mr-auto' : 'lg:ml-auto'
                    }`}
                  >
                    <img
                      src={card.image}
                      alt={card.imageAlt}
                loading="lazy"
                      width="584"
                      height="629"
                      className="innovation-card-image block w-full object-cover opacity-100 lg:aspect-[584/629] lg:h-auto lg:shrink-0"
              />
            </div>
          </div>

                <div
                  className={`innovation-card-copy w-full ${
                    imageLeft ? 'lg:justify-self-start' : 'lg:order-1 lg:justify-self-end'
                  }`}
                >
                  {imageLeft ? (
                      <div className="hidden w-fit max-w-full py-[3%] text-left lg:block lg:-translate-x-[10ch]">
                        <div className="innovation-card-title-stack items-start">
                          <InnovationAnimatedLine text={card.titleLines[0]} className="innovation-card-title-line" />
                          <InnovationAnimatedLine text={card.titleLines[1]} className="innovation-card-title-line" />
                          <div className="flex items-start gap-x-[0.9em]">
                            <InnovationAnimatedLine
                              text={card.titleLines[2]}
                              className="innovation-card-title-line shrink-0"
                            />
                            <p className={`${innovationCardBodyClass} ${index === 0 ? 'max-w-[min(100%,28rem)]' : index === 2 ? 'max-w-[min(100%,26rem)]' : 'max-w-[min(100%,24rem)]'}`}>
                              {(card.bodyLines || [card.body]).map((line) => (
                                <InnovationAnimatedLine
                                  key={line}
                                  text={line.replaceAll('\u00A0', ' ')}
                                  className="innovation-card-body-line block whitespace-nowrap align-middle"
                                />
                              ))}
                            </p>
            </div>
          </div>
        </div>
                    ) : (
                      <div className="ml-auto hidden w-fit max-w-full py-[3%] text-right lg:block lg:translate-x-[10ch]">
                        <div className="innovation-card-title-stack items-end">
                          <InnovationAnimatedLine text={card.titleLines[0]} className="innovation-card-title-line" />
                          <InnovationAnimatedLine text={card.titleLines[1]} className="innovation-card-title-line" />
                          <div className="flex items-start justify-end gap-x-[0.9em]">
                            <p className={`${innovationCardBodyClass} text-right`}>
                              {(card.bodyLines || [card.body]).map((line) => (
                                <InnovationAnimatedLine
                                  key={line}
                                  text={line.replaceAll('\u00A0', ' ')}
                                  className="innovation-card-body-line block whitespace-nowrap align-middle"
                                />
                              ))}
                            </p>
                            <InnovationAnimatedLine
                              text={card.titleLines[2]}
                              className="innovation-card-title-line shrink-0"
              />
            </div>
          </div>
        </div>
                    )}
                  <InnovationMobileCopy card={card} />
        </div>
              </article>
            );
          })}
        </div>
      </section>
      </div>

      <SectionContainer
        ref={promiseStripRef}
        as="section"
        className="home-reveal-on-scroll neoterra-promise-strip relative z-[1] mx-auto block h-[157px] w-full max-w-full min-h-0 shrink-0 overflow-hidden bg-[#FFF7EA] pt-0 lg:h-[252px]"
        aria-label="Group 27"
        maxWidthClass="max-w-none"
      >
        <img
          src={frame205Logo}
          alt="Neoterra Promise"
          width={452}
          height={203}
          className="pointer-events-none absolute left-1/2 top-1/2 z-[1] block h-[104px] w-[232px] -translate-x-1/2 -translate-y-1/2 object-contain opacity-100 lg:h-auto lg:w-[339px]"
          loading="lazy"
        />
      </SectionContainer>
      </div>

      <div
        ref={featuredRevealRef}
        className="featured-wipe-runway relative z-[1] w-full"
        style={{ height: `${(FEATURED_CAROUSEL_SLIDES.length - 1) * 100}dvh` }}
        aria-hidden="true"
      />

      <div ref={featuredRevealPinHostRef} className="featured-reveal-pin-host relative w-full">
      <div ref={featuredRevealPinRef} className="featured-reveal-pin w-full overflow-hidden">
      <SectionContainer
        as="section"
        className="featured-hero-sticky relative mx-auto h-full min-h-[100dvh] w-full max-w-full overflow-hidden bg-[#FFF7EA] p-0"
        aria-label="Featured hero"
        maxWidthClass="max-w-none"
        style={{ height: '100dvh', minHeight: '100dvh' }}
      >
        <div className="absolute inset-0 z-[1] overflow-hidden">
          {FEATURED_CAROUSEL_SLIDES.map((slide, idx) => {
            const titleLines = idx === 0 ? FEATURED_INTRO_COPY.titleLines : slide.titleLines;
            const subtitleLines = idx === 0 ? FEATURED_INTRO_COPY.subtitleLines : slide.subtitleLines;
            const showCopy = idx === 0
              ? Boolean(titleLines?.length && subtitleLines?.length)
              : Boolean(!slide.hideCopy && titleLines?.length && subtitleLines?.length);

            return (
              <div
                key={slide.id}
                ref={(el) => {
                  featuredSlideElsRef.current[idx] = el;
                }}
                className={`featured-carousel-slide absolute inset-0 will-change-[clip-path]${idx === 0 ? ' featured-slide--intro' : ''}`}
              style={{
                  zIndex: idx,
                  clipPath: FEATURED_CAROUSEL_HIDDEN_CLIP,
                  transition: 'none',
                }}
                  >
                    <img
                  src={slide.image}
                  alt={slide.alt}
                  aria-hidden
                  className="block h-full w-full object-cover object-center"
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      fetchPriority={idx === 0 ? 'high' : 'auto'}
                />
                {showCopy ? (
                  <FeaturedHeroCopy
                    ref={(el) => {
                      featuredCopyElsRef.current[idx] = el;
                    }}
                    titleLines={titleLines}
                    subtitleLines={
                      idx === 0
                        ? FEATURED_INTRO_COPY.subtitleLines
                        : (viewportWidth < 1024 && slide.mobileSubtitleLines) || slide.subtitleLines
                    }
                    copyTop={0}
                  />
                ) : null}
                  </div>
                );
              })}
            </div>
      </SectionContainer>
          </div>
      </div>
        </div>

      <div className="eira-experience relative w-full">
      <div className="eira-scroll-curtain relative z-[20] w-full">
      <SectionContainer
        ref={luxuryThoughtfullyRef}
        as="section"
        className="luxury-thoughtfully-section relative z-[1] mx-auto flex h-[470px] min-h-[470px] w-full max-w-full items-center justify-center overflow-hidden bg-[#FFF7EA] px-0 py-0 lg:h-[100vh] lg:min-h-[100vh]"
        aria-label="Luxury done thoughtfully"
        maxWidthClass="max-w-none"
      >
        <img
          src={vectorGraphic}
          alt=""
          width={715}
          height={715}
          className="luxury-thoughtfully-bg pointer-events-none absolute left-1/2 top-1/2 z-[1] block h-auto w-[min(536px,82vw)] -translate-x-1/2 -translate-y-1/2 object-contain opacity-100 lg:w-[536px]"
          loading="lazy"
          aria-hidden="true"
        />
        <div className="relative z-[2] flex flex-col items-center justify-center text-center text-[#BC8639]">
          <p className="luxury-thoughtfully-kicker m-0 font-['Poppins',sans-serif] text-[30px] font-semibold uppercase leading-[32.81px] tracking-[17px] align-middle">
            Luxury done
          </p>
          <p className="luxury-thoughtfully-title m-0 mt-[16px] font-['Poppins',sans-serif] text-[clamp(48px,6.25vw,80px)] font-medium uppercase leading-[100%] tracking-[17px] align-middle">
            thoughtfully
          </p>
        </div>
      </SectionContainer>
      </div>

      <div
        ref={eiraWipeRunwayRef}
        className="eira-wipe-runway relative z-[1] w-full"
        style={{ height: `${EIRA_SHOWCASE_SLIDES.length * 100}dvh` }}
              aria-hidden="true"
            />

      <div ref={eiraRevealPinHostRef} className="eira-reveal-pin-host relative w-full">
      <div ref={eiraRevealPinRef} className="eira-reveal-pin w-full overflow-hidden">
      <SectionContainer
        as="section"
        className="relative mx-auto h-full min-h-[100dvh] w-full max-w-full overflow-hidden bg-[#0a0a0a] p-0"
        aria-label="Eira showcase"
        maxWidthClass="max-w-none"
        style={{ height: '100dvh', minHeight: '100dvh' }}
      >
        <div className="absolute inset-0 z-[1] overflow-hidden">
          {EIRA_SHOWCASE_SLIDES.map((slide, idx) => (
              <div
                key={slide.id}
                ref={(el) => {
                  eiraSlideElsRef.current[idx] = el;
                }}
                className="eira-carousel-slide absolute inset-0 will-change-[clip-path]"
                style={{
                  zIndex: idx,
                  clipPath: idx === 0 ? CAROUSEL_REVEALED_CLIP : FEATURED_CAROUSEL_HIDDEN_CLIP,
                  transition: 'none',
                }}
              >
                <img
                  src={viewportWidth < 1024 ? (EIRA_SHOWCASE_MOBILE_IMAGES[slide.id] || slide.image) : slide.image}
                  alt={slide.alt}
                  aria-hidden
                  className="block h-full w-full object-cover"
                  loading={idx === 0 ? 'eager' : 'lazy'}
                />
                <EiraShowcaseSlideOverlay slideId={slide.id} interactive={viewportWidth < 1024} />
              </div>
          ))}
        </div>
      </SectionContainer>
      </div>
      </div>
      </div>

      <div className={`site-page-footer-wrap relative z-[30] isolate pt-0 ${viewportWidth >= 1024 ? 'bg-[#162D24]' : 'bg-[#FFFFFF]'}`}>
        <HomeContactFooter variant={viewportWidth >= 1024 ? 'home' : 'default'} />
      </div>

      <MailingListModal
        open={mailOpen}
        closing={mailClosing}
        onClose={closeMailModal}
        onSubmit={handleMailSubmit}
        submitting={contactSubmitting}
        fullName={fullName}
        setFullName={setFullName}
        email={email}
        setEmail={setEmail}
        phone={phone}
        setPhone={setPhone}
        promotionalConsent={promotionalConsent}
        setPromotionalConsent={setPromotionalConsent}
        idPrefix="home-mail"
      />
    </div>
  );
};

export default LandingPage;
